import { Dialog, DialogContent, DialogTitle, IconButton, Typography, Box } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircleOutline, ErrorOutline } from '@mui/icons-material';

interface ProfileModalResponseProps {
	open: boolean;
	onClose: () => void;
	isSuccess: boolean;
	successMessage?: string;
	errorMessage?: string;
	responseData?: string;
}

const ProfileModalResponse = ({
	open,
	onClose,
	isSuccess,
	successMessage = 'Profile Updated Successfully',
	errorMessage = 'Failed to update profile',
	responseData,
}: ProfileModalResponseProps) => (
	<Dialog
		open={open}
		onClose={onClose}
		PaperProps={{
			component: motion.div,
			initial: { scale: 0.8, opacity: 0 },
			animate: { scale: 1, opacity: 1 },
			exit: { scale: 0.8, opacity: 0 },
			transition: { type: 'spring', damping: 20, stiffness: 300 },
			sx: {
				borderRadius: '20px',
				boxShadow: '0px 4px 4px rgba(0,0,0,0.1)',
				width: '491px',
				maxWidth: '90vw',
				overflow: 'hidden',
			},
		}}
	>
		<DialogTitle sx={{ textAlign: 'center', pt: 4, pb: 2 }}>
			<motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2 }}>
				{isSuccess ? (
					<CheckCircleOutline sx={{ fontSize: 80, color: '#2e7d32' }} color="success" />
				) : (
					<ErrorOutline sx={{ fontSize: 80, color: '#d32f2f' }} color="error" />
				)}
			</motion.div>
		</DialogTitle>

		<DialogContent sx={{ textAlign: 'center', pb: 2 }}>
			<AnimatePresence mode="wait">
				<motion.div
					key={isSuccess ? 'success' : 'error'}
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					exit={{ opacity: 0, y: -20 }}
					transition={{ duration: 0.3 }}
				>
					<Typography
						variant="h5"
						sx={{
							color: isSuccess ? '#2e7d32' : '#d32f2f',
							fontWeight: 600,
							mb: 2,
						}}
					>
						{isSuccess ? successMessage : errorMessage}
					</Typography>

					<Typography variant="body1" sx={{ mb: 2 }}>
						{isSuccess
							? 'The vendor profile has been successfully updated.'
							: 'Please try again or contact support.'}
					</Typography>

					{responseData && (
						<Box
							component={motion.div}
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							transition={{ delay: 0.2 }}
							sx={{
								backgroundColor: isSuccess
									? 'rgba(46, 125, 50, 0.1)'
									: 'rgba(211, 47, 47, 0.1)',
								borderRadius: '8px',
								p: 2,
								mt: 2,
								textAlign: 'left',
							}}
						>
							<Typography
								variant="caption"
								sx={{ display: 'block', fontWeight: 600, mb: 1 }}
							>
								Response Details:
							</Typography>
							<Typography
								variant="body2"
								component="pre"
								sx={{
									whiteSpace: 'pre-wrap',
									wordBreak: 'break-word',
									fontFamily: 'monospace',
									fontSize: '0.8rem',
								}}
							>
								{responseData}
							</Typography>
						</Box>
					)}
				</motion.div>
			</AnimatePresence>
		</DialogContent>

		<IconButton
			onClick={onClose}
			sx={{
				position: 'absolute',
				right: 16,
				top: 16,
			}}
		>
			×
		</IconButton>
	</Dialog>
);

export default ProfileModalResponse;
